import React, { useState, useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import { useDataManagement } from '../ferramentas/RequisitarAPI.ts';
import useDebounce from '../ferramentas/useDebounce.js'; // Reutilizando o hook de debounce

// Importando os componentes modularizados
import { ErrorMessage, NoDataMessage } from './components/TableFeedback';
import SortIndicator from './components/SortIndicator';

const ClientesSection = ({ styles, formatCurrency, initialDateRange }) => {
    // Hook para interagir com a API
    const { reports, load, error } = useDataManagement();
    
    // Estado principal de filtros, que dispara a busca na API
    const [filters, setFilters] = useState({
        start_date: initialDateRange.inicio,
        final_date: initialDateRange.fim,
        customers: '', // Adaptado para 'customers'
        page: 1,
        limit: 10,
        sort: { by: 'total', order: 'desc' },
    });

    // --- LÓGICA DE DEBOUNCE ---
    // Estado para o valor do input de cliente, que atualiza instantaneamente na UI
    const [customerInput, setCustomerInput] = useState(''); 
    // Cria uma versão "atrasada" do valor do input
    const debouncedCustomerInput = useDebounce(customerInput, 500); 

    // Estados de controle de UI e dados
    const [reportData, setReportData] = useState(null);
    const [displayedRows, setDisplayedRows] = useState([]);

    // Refs para o gráfico
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);

    // --- EFEITOS (LÓGICA) ---

    // Efeito para atualizar os filtros principais quando o valor "atrasado" do input muda
    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            customers: debouncedCustomerInput,
            page: 1 
        }));
    }, [debouncedCustomerInput]);

    // 1. Busca os dados do relatório sempre que os filtros (filters) mudam
    useEffect(() => {
        const fetchReport = async () => {
            const customersArray = filters.customers 
                ? filters.customers.split(',').map(c => c.trim()).filter(Boolean) 
                : [];

            const apiFilters = {
                start_date: filters.start_date || undefined,
                final_date: filters.final_date || undefined,
                customers: customersArray.length > 0 ? customersArray : undefined, // Adaptado para 'customers'
                page: filters.page,
                limit: parseInt(filters.limit, 10),
                // sort_by: filters.sort.by,
                // sort_order: filters.sort.order,
            };
            
            // Adaptado para chamar reports.clients
            const response = await reports.clients(apiFilters); 
            
            if (!response.ok) {
                console.error('Erro ao buscar relatório de clientes:', response.message);
            }
            setReportData(response.ok ? response.data : null);
        };
        fetchReport();
    }, [filters, reports]);

    // 2. Aplica a ordenação local nos dados da página atual
    useEffect(() => {
        if (!reportData || !reportData.data) {
            setDisplayedRows([]);
            return;
        }
        const sortedPageData = [...reportData.data].sort((a, b) => {
            const valA = a[filters.sort.by];
            const valB = b[filters.sort.by];

            if (typeof valA === 'string') {
                return filters.sort.order === 'asc' 
                    ? valA.localeCompare(valB) 
                    : valB.localeCompare(valA);
            }
            if (valA < valB) return filters.sort.order === 'asc' ? -1 : 1;
            if (valA > valB) return filters.sort.order === 'asc' ? 1 : -1;
            return 0;
        });
        setDisplayedRows(sortedPageData);
    }, [reportData, filters.sort]);

    // 3. Renderiza e atualiza o gráfico
    useEffect(() => {
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }
        if (chartRef.current && reportData?.data?.length > 0) {
            const ctx = chartRef.current.getContext('2d');
            const chartData = [...reportData.data].sort((a, b) => b.total - a.total);
            const chartLabels = chartData.map(item => item.customer); // Adaptado para 'customer'
            const chartValues = chartData.map(item => item.total);

            chartInstanceRef.current = new Chart(ctx, {
                type: 'bar',
                data: { 
                    labels: chartLabels, 
                    datasets: [{ 
                        label: 'Faturamento por Cliente', // Legenda adaptada
                        data: chartValues, 
                        backgroundColor: '#008fb3', // Usando a cor secundária do tema para diferenciar
                        borderColor: '#33c2e3',
                        borderWidth: 1
                    }] 
                },
                options: {
                    responsive: true, maintainAspectRatio: false,
                    scales: { y: { beginAtZero: true, ticks: { color: '#9ca3af' }, grid: { color: '#374151' } }, x: { ticks: { color: '#9ca3af' }, grid: { color: '#374151' } } },
                    plugins: { legend: { labels: { color: '#d1d5db' } } }
                }
            });
        }
        return () => { if (chartInstanceRef.current) chartInstanceRef.current.destroy(); };
    }, [reportData]);


    // --- MANIPULADORES DE EVENTOS ---

    const handleNonDebouncedFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value,
            page: 1
        }));
    };
    const handleSort = (columnName) => setFilters(prev => ({...prev, sort: {by: columnName, order: prev.sort.by === columnName && prev.sort.order === 'desc' ? 'asc' : 'desc' }, page: 1 }));
    const handleNextPage = () => setFilters(prev => ({ ...prev, page: prev.page + 1 }));
    const handlePreviousPage = () => setFilters(prev => ({ ...prev, page: prev.page - 1 }));


    // --- FUNÇÃO AUXILIAR DE RENDERIZAÇÃO ---

    const totalPages = reportData ? Math.ceil(reportData.total / filters.limit) : 1;
    
    // --- RENDERIZAÇÃO DO COMPONENTE (JSX) ---

    return (
        <div id="clientes" className={`${styles.tabContent} ${styles.activeContent} ${styles.card}`}>
            <h2 className={styles.cardTitle}>Relatório de Faturamento por Cliente</h2>
            
            <div className={`${styles.filterSection} ${styles.colsMd2} ${styles.colsLg4}`}>
                <div>
                    <label>Data Início</label>
                    <input type="date" name="start_date" value={filters.start_date} onChange={handleNonDebouncedFilterChange} />
                </div>
                <div>
                    <label>Data Fim</label>
                    <input type="date" name="final_date" value={filters.final_date} onChange={handleNonDebouncedFilterChange} />
                </div>
                <div>
                    <label>Cliente(s) (separados por vírgula)</label>
                    <input 
                        type="text" 
                        name="customers" 
                        value={customerInput} 
                        onChange={(e) => setCustomerInput(e.target.value)}
                        placeholder="Ex: João Silva, Maria Souza"
                    />
                </div>
                <div>
                    <label>Itens por Página</label>
                    <select name="limit" value={filters.limit} onChange={handleNonDebouncedFilterChange}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="250">250</option>
                    </select>
                </div>
            </div>

            <div className={styles.tableResponsiveWrapper}>
                <table id="clientes-table" className={`${styles.table} ${styles.tableSortable}`}>
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('customer')}>Cliente <SortIndicator column="customer" sortConfig={filters.sort} /></th>
                            <th onClick={() => handleSort('cards')}>Compromissos <SortIndicator column="cards" sortConfig={filters.sort} /></th>
                            <th onClick={() => handleSort('total')}>Faturamento Total <SortIndicator column="total" sortConfig={filters.sort} /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {load.isLoading ? 
                            <div id="loading-overlay">
                                <div>
                                <div id="loading-spinner"></div>
                                <p>{load.reason}</p>
                                </div>
                            </div>
                         : error ? <ErrorMessage error={error} styles={styles} />
                         : displayedRows.length > 0 ? (
                            displayedRows.map((row) => (
                                <tr key={row.customer}>
                                    <td>{row.customer}</td>
                                    <td>{row.cards}</td>
                                    <td>{formatCurrency(row.total)}</td>
                                </tr>
                            ))
                        ) : <NoDataMessage styles={styles} />}
                    </tbody>
                    {!load.isLoading && !error && reportData?.data?.length > 0 && (
                        <tfoot>
                            <tr>
                                <td colSpan="2">Total Geral {reportData.total} Cliente(s)</td>
                                <td>
                                    {formatCurrency(reportData.total_financial)} 
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {!load.isLoading && !error && reportData && reportData.total > filters.limit && (
                <div className={styles.paginationControls}>
                    <button onClick={handlePreviousPage} disabled={filters.page <= 1}>Anterior</button>
                    <span>Página {filters.page} de {totalPages}</span>
                    <button onClick={handleNextPage} disabled={filters.page >= totalPages}>Próxima</button>
                </div>
            )}
            
            <div className={styles.chartContainer}>
                <canvas ref={chartRef} id="clientesChart"></canvas>
            </div>
        </div>
    );
};

export default ClientesSection;